import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { max, min, parseISO } from "date-fns";
import { toDateKey } from "@/features/addons/hooks/use-diet-addon-modal";
import type {
  EventViewItemListType,
  GTMCartItemType,
} from "@/features/analytics/types/event.type";
import type { SubItem } from "@/features/cart/types/sub-item.type";
import type { SubItemsPricing } from "../types/sub-Items-pricing.type";

export const ADDON_ITEM_VARIANT = "addon";

export type AddonGtmListItem =
  EventViewItemListType["ecommerce"]["items"][number];

const POLISH_CHARS: Record<string, string> = {
  ą: "a",
  ć: "c",
  ę: "e",
  ł: "l",
  ń: "n",
  ó: "o",
  ś: "s",
  ź: "z",
  ż: "z",
};

// Category name as it appears in item_list_name: lowercase, diacritics folded,
// spaces turned into dashes ("Przekąski i Inne" → "przekaski-i-inne").
export const slugifyAddonCategoryName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (char) => POLISH_CHARS[char] ?? char)
    .trim()
    .replace(/\s+/g, "-");

export const getAddonUnitPrice = (addon: AddonDietAddonsRead): number =>
  addon.promoPrice ?? addon.price;

export const getAddonSelectionValue = (
  addon: AddonDietAddonsRead,
  quantity: number,
): number => getAddonUnitPrice(addon) * quantity;

// One addon selection: a delivery day with the quantity attached to it. Keyed
// the same way the cart quote keys its sub-item entries — by (addon id, date).
export type AddonQuantityEntry = { date: string; quantity: number };

// Unit price the cart really charges for an addon, deposit excluded: the quote
// reports entry totals with the deposit inside, while GTM `price` is the
// product price alone. The catalog price ignores every cart-level discount (a
// coupon among them), so it serves only entries the quote does not cover — a
// selection the cart has not re-quoted yet (MPL-3873).
export const resolveAddonUnitPrice = ({
  addon,
  entries,
  pricing,
}: {
  addon: AddonDietAddonsRead;
  entries: AddonQuantityEntry[];
  pricing?: SubItemsPricing | null;
}): number => {
  if (!pricing) {
    return getAddonUnitPrice(addon);
  }

  const quoted = new Map(
    pricing.items.map((item) => [`${item.id}|${item.date}`, item]),
  );

  let charged = 0;
  let quantity = 0;

  for (const entry of entries) {
    const item = quoted.get(`${addon.id}|${entry.date}`);
    charged += item
      ? item.final.gross - item.deposit.gross
      : getAddonUnitPrice(addon) * entry.quantity;
    quantity += entry.quantity;
  }

  return quantity > 0 ? charged / quantity : getAddonUnitPrice(addon);
};

export const findAddonCategoryName = (
  categories: DietAddonGroupDietAddonsRead[],
  addonId: string,
): string | undefined =>
  [...categories]
    .filter((category) => category.addons.some((addon) => addon.id === addonId))
    .sort((a, b) => a.position - b.position)
    .at(0)
    ?.name.toLowerCase();

export const findAddonInCategories = (
  categories: DietAddonGroupDietAddonsRead[],
  addonId: string,
): AddonDietAddonsRead | undefined => {
  for (const category of categories) {
    const found = category.addons.find((addon) => addon.id === addonId);
    if (found) {
      return found;
    }
  }
  return undefined;
};

type BuildAddonGtmListItemParams = {
  addon: AddonDietAddonsRead;
  cateringName?: string;
  index: number;
};

export const buildAddonGtmListItem = ({
  addon,
  cateringName,
  index,
}: BuildAddonGtmListItemParams): AddonGtmListItem => ({
  item_id: addon.id,
  item_name: addon.name,
  price: getAddonUnitPrice(addon),
  index,
  ...(cateringName ? { item_brand: cateringName } : {}),
});

type BuildAddonGtmListItemsParams = {
  addons: AddonDietAddonsRead[];
  cateringName?: string;
};

export const buildAddonGtmListItems = ({
  addons,
  cateringName,
}: BuildAddonGtmListItemsParams): AddonGtmListItem[] =>
  addons.map((addon, position) =>
    buildAddonGtmListItem({ addon, cateringName, index: position + 1 }),
  );

type BuildAddonGtmItemParams = {
  addon: AddonDietAddonsRead;
  cateringName?: string;
  categoryName?: string;
  selectedDates: Date[];
  quantity: number;
  // Price charged per unit, resolved from the cart quote by the caller. Absent
  // outside the cart (the addon dialog), where the catalog price is the price.
  unitPrice?: number;
};

export const buildAddonGtmItem = ({
  addon,
  cateringName,
  categoryName,
  selectedDates,
  quantity,
  unitPrice,
}: BuildAddonGtmItemParams): GTMCartItemType => {
  const uniqueDates = [
    ...new Map(selectedDates.map((date) => [toDateKey(date), date])).values(),
  ];

  return {
    item_id: addon.id,
    item_name: addon.name,
    item_variant: ADDON_ITEM_VARIANT,
    price: unitPrice ?? getAddonUnitPrice(addon),
    quantity,
    duration: uniqueDates.length,
    ...(cateringName ? { item_brand: cateringName } : {}),
    ...(categoryName ? { item_category: categoryName } : {}),
    ...(uniqueDates.length > 0
      ? {
          item_category3: min(uniqueDates).getTime(),
          item_category4: max(uniqueDates).getTime(),
        }
      : {}),
  };
};

type BuildAddonGtmItemsForCartItemParams = {
  subItems?: SubItem[];
  categories: DietAddonGroupDietAddonsRead[];
  cateringName?: string;
  // The position's addon quote, so `price` reports what the cart charges after
  // discounts instead of the catalog list price (MPL-3873).
  subItemsPricing?: SubItemsPricing | null;
};

export const buildAddonGtmItemsForCartItem = ({
  subItems,
  categories,
  cateringName,
  subItemsPricing,
}: BuildAddonGtmItemsForCartItemParams): GTMCartItemType[] => {
  const byAddonId = new Map<
    string,
    { quantity: number; dates: Date[]; entries: AddonQuantityEntry[] }
  >();

  for (const subItem of subItems ?? []) {
    const current = byAddonId.get(subItem.id) ?? {
      quantity: 0,
      dates: [],
      entries: [],
    };
    current.quantity += subItem.quantity;
    current.dates.push(parseISO(subItem.date));
    current.entries.push({ date: subItem.date, quantity: subItem.quantity });
    byAddonId.set(subItem.id, current);
  }

  const items: GTMCartItemType[] = [];
  for (const [addonId, { quantity, dates, entries }] of byAddonId) {
    const addon = findAddonInCategories(categories, addonId);
    if (!addon) {
      continue;
    }
    items.push(
      buildAddonGtmItem({
        addon,
        cateringName,
        categoryName: findAddonCategoryName(categories, addonId),
        quantity,
        selectedDates: dates,
        unitPrice: resolveAddonUnitPrice({
          addon,
          entries,
          pricing: subItemsPricing,
        }),
      }),
    );
  }

  return items;
};
